import React, { useState, useEffect } from 'react';
import {
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  Divider,
  Slider,
  FormHelperText,
  Tabs,
  Tab
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { agentService, resourceService } from '../services/api';
import AgentGoals from './AgentGoals';
import VoicemailDetectionSettings from './VoicemailDetectionSettings';

const AgentFormWithTabs = ({ agent = null, mode = 'create' }) => {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    voiceId: '',
    webhook_url: '',
    knowledgeBaseId: '',
    selectedFunctions: ['end_call'], // Always include end_call by default
    actions: {
      duringCall: [],
      afterCall: []
    },
    configuredActions: {},
    settings: {
      voicemailDetection: {
        enabled: false,
        action: 'hangup',
        message: 'Hey {{user_name}}, sorry we could not reach you directly. Please give us a callback if you can.'
      }
    },
    llmConfig: {
      model: '',
      s2sModel: '',
      modelTemperature: 0.7,
      modelHighPriority: false,
      generalPrompt: 'You are a helpful AI assistant.'
    }
  });
  
  const [resources, setResources] = useState({
    voices: [],
    models: [],
    s2sModels: []
  });
  
  const [knowledgeBases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [error, setError] = useState('');
  const [compatibilityError, setCompatibilityError] = useState('');
  const [modelType, setModelType] = useState('standard');
  const [success, setSuccess] = useState('');

  // Fetch available resources when component mounts
  useEffect(() => {
    const fetchResources = async () => {
      try {
        setResourcesLoading(true);
        const data = await resourceService.getAvailableResources();
        
        const models = data.availableModels?.models || [];
        const s2sModels = data.availableModels?.s2sModels || [];
        
        setResources({
          voices: data.voices || [],
          models,
          s2sModels
        });
        
        // Set default values for new agents
        if (mode === 'create' && data.voices && data.voices.length > 0) {
          const compatibleVoice = data.voices.find(v => 
            v.provider === 'openai' || 
            (v.provider !== 'elevenlabs' && v.provider !== '11labs')
          ) || data.voices[0];
          
          setFormData(prev => ({
            ...prev,
            voiceId: compatibleVoice.voice_id,
            llmConfig: {
              ...prev.llmConfig,
              model: models.length > 0 ? models[0] : '',
              s2sModel: ''
            }
          }));
        }
        
        setResourcesLoading(false);
      } catch (error) {
        console.error('Failed to fetch resources:', error);
        setError('Failed to load voices and models. Please refresh the page.');
        setResourcesLoading(false);
      }
    };
    
    fetchResources();
  }, [mode]);

  // Load agent data for editing
  useEffect(() => {
    if (mode === 'edit' && agent && !resourcesLoading) {
      const isRealtime = agent.llmConfig?.s2sModel;
      const currentModelType = isRealtime ? 'realtime' : 'standard';
      setModelType(currentModelType);
      
      setFormData({
        name: agent.name,
        description: agent.description,
        voiceId: agent.voiceId,
        webhook_url: agent.webhookConfig?.url || '',
        knowledgeBaseId: agent.knowledgeBaseId || '',
        selectedFunctions: agent.selectedFunctions || [],
        actions: agent.actions || {
          duringCall: [],
          afterCall: []
        },
        configuredActions: agent.configuredActions || {},
        settings: {
          voicemailDetection: agent.settings?.voicemailDetection || {
            enabled: false,
            action: 'hangup',
            message: 'Hey {{user_name}}, sorry we could not reach you directly. Please give us a callback if you can.'
          }
        },
        // Welcome message configuration
        welcomeMessageType: agent.llmConfig?.welcomeMessageType || 'user_initiate',
        welcomeMessage: agent.llmConfig?.welcomeMessage || 'Hello, how can I help you today?',
        pauseBeforeSpeaking: agent.llmConfig?.pauseBeforeSpeaking || 0,
        beginAfterUserSilence: agent.llmConfig?.beginAfterUserSilence !== undefined ? agent.llmConfig.beginAfterUserSilence : false,
        beginAfterUserSilenceMs: agent.llmConfig?.beginAfterUserSilenceMs || 5000,
        llmConfig: {
          model: agent.llmConfig?.model || '',
          s2sModel: agent.llmConfig?.s2sModel || '',
          modelTemperature: agent.llmConfig?.modelTemperature || 0.7,
          modelHighPriority: agent.llmConfig?.modelHighPriority || false,
          generalPrompt: agent.llmConfig?.generalPrompt || 'You are a helpful AI assistant.'
        }
      });
    }
  }, [agent, mode, resourcesLoading]);

  // Check compatibility when voice or s2s model changes
  useEffect(() => {
    const checkCompatibility = async () => {
      if (formData.voiceId && formData.llmConfig.s2sModel && modelType === 'realtime') {
        try {
          const result = await resourceService.checkCompatibility(
            formData.voiceId, 
            formData.llmConfig.s2sModel
          );
          
          if (!result.compatible) {
            setCompatibilityError(`This voice is not compatible with the selected speech-to-speech model. Please choose a different voice or use a standard model instead.`);
          } else {
            setCompatibilityError('');
          }
        } catch (error) {
          setCompatibilityError(error.response?.data?.message || 'Compatibility check failed');
        }
      } else {
        setCompatibilityError('');
      }
    };
    
    checkCompatibility();
  }, [formData.voiceId, formData.llmConfig.s2sModel, modelType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleLlmConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : 
               type === 'number' ? parseFloat(value) : value;
    
    setFormData({
      ...formData,
      llmConfig: {
        ...formData.llmConfig,
        [name]: val
      }
    });
  };

  const handleModelTypeChange = (type) => {
    setModelType(type);
    
    if (type === 'standard') {
      setFormData({
        ...formData,
        llmConfig: {
          ...formData.llmConfig,
          s2sModel: ''
        }
      });
    } else {
      setFormData({
        ...formData,
        llmConfig: {
          ...formData.llmConfig,
          model: ''
        }
      });
    }
  };

  const handleRefreshResources = async () => {
    try {
      setResourcesLoading(true);
      setError('');
      
      const data = await resourceService.refreshResources();
      
      const models = data.availableModels?.models || [];
      const s2sModels = data.availableModels?.s2sModels || [];
      
      setResources({
        voices: data.voices || [],
        models,
        s2sModels
      });
      
      setResourcesLoading(false);
    } catch (error) {
      console.error('Failed to refresh resources:', error);
      setError('Failed to refresh voices and models. Please try again.');
      setResourcesLoading(false);
    }
  };

  const handleFormDataChange = (newFormData) => {
    setFormData(newFormData);
  };

  const handleVoicemailDetectionChange = (voicemailDetection) => {
    setFormData({
      ...formData,
      settings: {
        ...formData.settings,
        voicemailDetection
      }
    });
  };

  const handleKnowledgeBaseChange = (knowledgeBaseId) => {
    setFormData({
      ...formData,
      knowledgeBaseId
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (compatibilityError) {
      setError('Please resolve compatibility issues before submitting.');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const submissionData = {
        name: formData.name,
        description: formData.description,
        voiceId: formData.voiceId,
        webhook_url: formData.webhook_url,
        knowledgeBaseId: formData.knowledgeBaseId,
        selectedFunctions: formData.selectedFunctions,
        actions: formData.actions,
        configuredActions: formData.configuredActions,
        settings: formData.settings,
        // Welcome message configuration
        welcomeMessageType: formData.welcomeMessageType || 'user_initiate',
        welcomeMessage: formData.welcomeMessage,
        pauseBeforeSpeaking: formData.pauseBeforeSpeaking || 0,
        beginAfterUserSilence: formData.beginAfterUserSilence !== undefined ? formData.beginAfterUserSilence : false,
        beginAfterUserSilenceMs: formData.beginAfterUserSilenceMs || 5000,
        llmConfig: { 
          ...formData.llmConfig,
          generalPrompt: formData.generalPrompt || formData.llmConfig.generalPrompt
        }
      };
      
      // Remove the model that's not being used based on modelType
      if (modelType === 'standard') {
        delete submissionData.llmConfig.s2sModel;
      } else {
        delete submissionData.llmConfig.model;
      }
      
      let result;
      if (mode === 'create') {
        result = await agentService.createAgent(submissionData);
        setSuccess('Agent created successfully!');
        setTimeout(() => {
          navigate(`/agents/${result.id}`);
        }, 1500);
      } else {
        result = await agentService.updateAgent(agent.id, submissionData);
        setSuccess('Agent updated successfully!');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to save agent:', err);
      setError(mode === 'create' ? 'Failed to create agent' : 'Failed to update agent');
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Group voices by provider
  const groupedVoices = resources.voices.reduce((acc, voice) => {
    const provider = voice.provider || 'Other';
    if (!acc[provider]) {
      acc[provider] = [];
    }
    acc[provider].push(voice);
    return acc;
  }, {});

  if (resourcesLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Agent Details
        return (
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Agent Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter a name for your agent"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Describe what this agent does"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Webhook URL (Optional)"
                name="webhook_url"
                value={formData.webhook_url}
                onChange={handleChange}
                placeholder="https://your-domain.com/webhook"
                helperText="Receive call events at this URL"
              />
            </Grid>

            {/* Voice Configuration */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Voice Configuration
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Voice</InputLabel>
                <Select
                  name="voiceId"
                  value={formData.voiceId}
                  onChange={handleChange}
                  label="Voice"
                >
                  {Object.entries(groupedVoices).map(([provider, voices]) => [
                    <MenuItem key={`header-${provider}`} disabled>
                      <Typography variant="subtitle2" color="text.secondary">
                        {provider.charAt(0).toUpperCase() + provider.slice(1)}
                      </Typography>
                    </MenuItem>,
                    ...voices.map(voice => (
                      <MenuItem key={voice.voice_id} value={voice.voice_id}>
                        {voice.voice_name} ({voice.gender}, {voice.accent})
                      </MenuItem>
                    ))
                  ])}
                </Select>
                <FormHelperText>
                  {resources.voices.length} voices available
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* LLM Configuration */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                LLM Configuration
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Model Type</FormLabel>
                <RadioGroup
                  row
                  value={modelType}
                  onChange={(e) => handleModelTypeChange(e.target.value)}
                >
                  <FormControlLabel
                    value="standard"
                    control={<Radio />}
                    label="Standard Model"
                  />
                  <FormControlLabel
                    value="realtime"
                    control={<Radio />}
                    label="Real-time Speech-to-Speech"
                  />
                </RadioGroup>
                <FormHelperText>
                  Choose one model type. Standard models are for text processing, while real-time models are for speech-to-speech conversations.
                </FormHelperText>
              </FormControl>
            </Grid>
            
            {modelType === 'standard' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Model</InputLabel>
                  <Select
                    name="model"
                    value={formData.llmConfig.model}
                    onChange={handleLlmConfigChange}
                    label="Model"
                  >
                    {resources.models.map(model => (
                      <MenuItem key={model} value={model}>
                        {model}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {resources.models.length} models available
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}
            
            {modelType === 'realtime' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Speech-to-Speech Model</InputLabel>
                  <Select
                    name="s2sModel"
                    value={formData.llmConfig.s2sModel}
                    onChange={handleLlmConfigChange}
                    label="Speech-to-Speech Model"
                  >
                    {resources.s2sModels.map(model => (
                      <MenuItem key={model} value={model}>
                        {model}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {resources.s2sModels.length} real-time models available
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>
                Temperature: {formData.llmConfig.modelTemperature}
              </Typography>
              <Slider
                name="modelTemperature"
                value={formData.llmConfig.modelTemperature}
                onChange={(e, value) => handleLlmConfigChange({
                  target: { name: 'modelTemperature', value }
                })}
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.5, label: '0.5' },
                  { value: 1, label: '1' }
                ]}
              />
              <FormHelperText>
                Controls randomness. Lower values are more deterministic, higher values more creative.
              </FormHelperText>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="modelHighPriority"
                    checked={formData.llmConfig.modelHighPriority}
                    onChange={handleLlmConfigChange}
                  />
                }
                label="High Priority Processing"
              />
              <FormHelperText>
                Enable for faster response times (may incur additional costs)
              </FormHelperText>
            </Grid>
          </Grid>
        );

      case 1: // Agent Goals
        return (
          <AgentGoals
            formData={formData}
            onFormDataChange={handleFormDataChange}
            knowledgeBases={knowledgeBases}
            onKnowledgeBaseChange={handleKnowledgeBaseChange}
          />
        );

      case 2: // Phone & Availability
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Phone & Availability
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Configure phone settings and availability options.
            </Typography>
            
            <Box mt={3}>
              <VoicemailDetectionSettings
                voicemailDetection={formData.settings?.voicemailDetection}
                onChange={handleVoicemailDetectionChange}
              />
            </Box>
          </Box>
        );

      case 3: // Dashboard & Logs
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Dashboard & Logs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This section will contain dashboard configuration and logging settings.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Card elevation={2}>
        <CardHeader
          title={mode === 'create' ? 'Create New Agent' : `Edit Agent: ${agent?.name}`}
          titleTypographyProps={{ variant: 'h4', component: 'h1' }}
          action={
            <Button
              variant="outlined"
              onClick={handleRefreshResources}
              disabled={resourcesLoading}
              startIcon={resourcesLoading ? <CircularProgress size={16} /> : null}
            >
              {resourcesLoading ? 'Refreshing...' : 'Refresh Resources'}
            </Button>
          }
        />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {compatibilityError && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {compatibilityError}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Agent Details" />
              <Tab label="Agent Goals" />
              <Tab label="Phone & Availability" />
              <Tab label="Dashboard & Logs" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box component="form" onSubmit={handleSubmit}>
            {renderTabContent()}
            
            {/* Save Button - Always visible */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/agents')}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={loading}
                disabled={!!compatibilityError}
              >
                {mode === 'create' ? 'Create Agent' : 'Update Agent'}
              </LoadingButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AgentFormWithTabs; 